/**
 * Debounce a function using a timeout.
 *
 * Based on David Walsh's debounce function.
 * @see https://davidwalsh.name/javascript-debounce-function
 *
 * Returns a function that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * `wait` milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing.
 *
 * @param func
 *   The function to debounce.
 * @param wait
 *   The number of milliseconds to wait before triggering the function.
 * @param immediate
 *   Set to true to trigger on the leading instead of the trailing edge.
 *
 * @returns
 *   A debounced function that returns a promise which resolves to the return
 *   value of `func` after it ran.
 */
export function debounceTimeout<A extends any[], R>(
  func: (...args: A) => PromiseLike<R> | R,
  wait: number,
  immediate: boolean,
): (...args: A) => Promise<R> {
  let timeout: ReturnType<typeof setTimeout> | undefined;

  return function (...args: A): Promise<R> {
    return new Promise((resolve) => {
      const later = function () {
        timeout = undefined;
        if (!immediate) {
          resolve(func(...args));
        }
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) {
        resolve(func(...args));
      }
    });
  };
}
